import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import { theme } from "config";
import { observer } from "mobx-react";
import { Choice, Task, Course } from "models";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TaskType } from "types";
import { EditorInput } from "./inputEditor";
import { TextField } from 'components/textField';
import { Icon } from '../../components/icon';


export interface IEditorQuizTaskProps {
  task:   Task,
  course: Course,
}

export const EditorQuizTask = observer((props: IEditorQuizTaskProps) => {
  const { task, course } = props;
  const { t } = useTranslation();

  return (
    <Paper
      key={task.id}
      elevation={0}
      sx={{ marginBottom: '1rem', padding: '3rem', borderRadius: '12px' }}
    >
      <Grid
        key={task.id}
        mb={3}
        gap={3}
        justifyContent='space-between'
        container
      >
        <Grid flexGrow={2} item>
          <FormControl sx={{ width: '100%' }}>
            <FormLabel>{t('question')}</FormLabel>
            <TextField
              multiline
              fullWidth
              debounced={true}
              delay={300}
              variant='filled'
              placeholder={t('type_question_placeholder')}
              defaultValue={task.question || ''}
              sx={{
                '& .MuiFilledInput-root': {
                  backgroundColor: '#fff',
                  padding: '1.2rem 1.6rem',
                  borderRadius: '6px',
                },
                '& .MuiFilledInput-root:hover': {
                  backgroundColor: '#fff',
                },
                '& .MuiFilledInput-root.Mui-focused': {
                  backgroundColor: '#fff',
                },
              }}
              onChange={(e) =>
                task.set('question', e.target.value, Boolean(e.target.value))
              }
            />
          </FormControl>
        </Grid>
        <Grid xs='auto' item>
          <Box display='flex' flexDirection='column'>
            <FormLabel>{t('answer_type')}</FormLabel>
            <Select
              fullWidth
              disabled={!course?.canEdit}
              variant='outlined'
              placeholder={t('answer_type_placeholder')}
              value={task.type}
              onChange={(e) => task.set('type', e.target.value, true)}
              sx={{
                '&.MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  backgroundColor: theme.palette.background.default,
                  width: '14rem',
                },
              }}
            >
              {Object.values(TaskType).map((task) => (
                <MenuItem key={task} value={task}>
                  {t(task)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>
      </Grid>
      {task.type === TaskType.Column && (
        <Grid
          key={task.id + '_columns'}
          mb={3}
          gap={3}
          justifyContent='space-between'
          container
        >
          {(task.columns || []).map((col) => (
            <Grid
              key={col.index}
              flexGrow={2}
              item
              border={1}
              borderRadius={4}
              p={2}
            >
              <FormControl sx={{ width: '100%' }}>
                <FormLabel>{t('title')}</FormLabel>
                <TextField
                  fullWidth
                  debounced={true}
                  delay={300}
                  variant='filled'
                  placeholder={t('type_column_placeholder')}
                  defaultValue={col.title || ''}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#fff',
                      padding: '1.2rem 1.6rem',
                      borderRadius: '6px',
                    },
                    '& .MuiFilledInput-root:hover': {
                      backgroundColor: '#fff',
                    },
                    '& .MuiFilledInput-root.Mui-focused': {
                      backgroundColor: '#fff',
                    },
                  }}
                  onChange={(e) => task.setColumnTitle(col, e.target.value)}
                  onBlur={(e) => task.setColumnTitle(col, e.target.value, true)}
                />
              </FormControl>
              <FormControl sx={{ width: '100%' }}>
                <FormLabel sx={{ marginTop: '1rem' }}>{t('text')}</FormLabel>
                <TextField
                  fullWidth
                  debounced={true}
                  delay={300}
                  variant='filled'
                  placeholder={t('type_column_placeholder')}
                  defaultValue={col.text || ''}
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: '#fff',
                      padding: '1.2rem 1.6rem',
                      borderRadius: '6px',
                    },
                    '& .MuiFilledInput-root:hover': {
                      backgroundColor: '#fff',
                    },
                    '& .MuiFilledInput-root.Mui-focused': {
                      backgroundColor: '#fff',
                    },
                  }}
                  onChange={(e) => task.setColumnText(col, e.target.value)}
                  onBlur={(e) => task.setColumnText(col, e.target.value, true)}
                />
              </FormControl>
              <Button
                color='secondary'
                startIcon={<Icon iconId='bin' />}
                disabled={!course?.canEdit}
                onClick={() => task.removeColumn(col, true)}
              >
                {t('delete_column')}
              </Button>
            </Grid>
          ))}
          {task.type === TaskType.Column && (
            <Button
              color='secondary'
              key='add-column'
              startIcon={<Icon iconId='add' />}
              onClick={() => task.addColumn()}
            >
              {t('add_column')}
            </Button>
          )}
        </Grid>
      )}
      <Box mb={3}>
        {task.choices.length > 0 &&
          ![TaskType.Input, TaskType.Column].includes(task.type) && (
            <Typography
              variant='body2'
              fontWeight='bold'
              textTransform='uppercase'
            >
              {t('correct')}
            </Typography>
          )}
        {task.type === TaskType.Input ? (
          <Box pr='2rem'>
            <TextField
              fullWidth
              debounced={true}
              delay={300}
              type='number'
              variant='standard'
              placeholder={t('numerical_answer_placeholder')}
              defaultValue={task.answer}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                task.set('answer', e.target.value, true)
              }}
            />
          </Box>
        ) : (
          task?.choices?.map((c: Choice) => (
            <Box
              key={c.id}
              display='flex'
              flexDirection='row'
              alignItems='center'
              marginBottom={2}
            >
              <>
                {task.type === TaskType.Column ? (
                  <Box display='flex' flexDirection='column' paddingRight={4}>
                    <Select
                      fullWidth
                      disabled={!task?.columns?.length}
                      variant='outlined'
                      placeholder={t('column_placeholder')}
                      value={c.column}
                      onChange={(e) =>
                        c.set('column', Number(e.target.value), true)
                      }
                      sx={{
                        '&.MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          backgroundColor: theme.palette.background.default,
                          width: '14rem',
                        },
                      }}
                    >
                      {Object.values(task?.columns || {}).map((col) => (
                        <MenuItem key={col.index} value={col.index}>
                          {t(col.title)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                ) : (
                  <FormControlLabel
                    sx={{ alignSelf: 'center' }}
                    label=''
                    control={
                      <Checkbox
                        checked={Boolean(c.is_correct)}
                        onChange={() =>
                          c.set('is_correct', !c.is_correct, true)
                        }
                      />
                    }
                  />
                )}
                <Box
                  display='flex'
                  flexDirection='row'
                  width='100%'
                  alignItems='center'
                >
                  <Box mr={2} sx={{ flex: 1 }}>
                    <EditorInput
                      underline
                      placeholder={t('quiz_answer_placeholder')}
                      fontWeight={theme.typography.fontWeightMedium}
                      defaultValue={c.text || ''}
                      onChange={(text) => c.set('text', text, true)}
                    />
                  </Box>
                  <IconButton onClick={() => task.removeChoice(c)}>
                    <Icon iconId='remove' />
                  </IconButton>
                </Box>
              </>
            </Box>
          ))
        )}
      </Box>
      {task.type !== TaskType.Input && (
        <Button
          key='add-answer'
          color='secondary'
          startIcon={<Icon iconId='add' />}
          onClick={() => task.addChoice()}
        >
          {t('add_answer')}
        </Button>
      )}
      <Grow in={course?.getCurStep?.quiz?.is_control_quiz} unmountOnExit>
        <Box mt={2}>
          <FormControl sx={{ width: '100%' }}>
            <FormLabel>{t('correct_answer')}</FormLabel>
            <TextField
              multiline
              fullWidth
              debounced={true}
              delay={300}
              variant='filled'
              placeholder={t('correct_answer_placeholder')}
              defaultValue={task.correct_feedback || ''}
              sx={{
                '& .MuiFilledInput-root': {
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  padding: '1.2rem 1.6rem',
                },
                '& .MuiFilledInput-root:hover': {
                  backgroundColor: '#fff',
                },
                '& .MuiFilledInput-root.Mui-focused': {
                  backgroundColor: '#fff',
                },
              }}
              onChange={(e) =>
                task.set('correct_feedback', e.target.value, true)
              }
            />
          </FormControl>
        </Box>
      </Grow>
      <Grow in={course?.getCurStep?.quiz?.is_control_quiz} unmountOnExit>
        <Box mt={2}>
          <FormControl sx={{ width: '100%' }}>
            <FormLabel>{t('incorrect_answer')}</FormLabel>
            <TextField
              multiline
              fullWidth
              debounced={true}
              delay={300}
              variant='filled'
              placeholder={t('incorrect_answer_placeholder')}
              defaultValue={task.incorrect_feedback || ''}
              sx={{
                '& .MuiFilledInput-root': {
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  padding: '1.2rem 1.6rem',
                },
                '& .MuiFilledInput-root:hover': {
                  backgroundColor: '#fff',
                },
                '& .MuiFilledInput-root.Mui-focused': {
                  backgroundColor: '#fff',
                },
              }}
              onChange={(e) =>
                task.set('incorrect_feedback', e.target.value, true)
              }
            />
          </FormControl>
        </Box>
      </Grow>
      <Box
        mt={4}
        display='flex'
        alignItems='center'
        justifyContent={
          task.type === TaskType.Input ? 'flex-end' : 'space-between'
        }
      >
        {![TaskType.Input, TaskType.Column].includes(task.type) && (
          <Box display='flex' alignItems='center' flexDirection='row' gap={1}>
            <Box minWidth='8rem'>
              <Typography>{t('answers_shown')}</Typography>
            </Box>
            <Box minWidth='8rem'>
              <TextField
                fullWidth
                disabled={!course?.canEdit}
                variant='outlined'
                type='number'
                placeholder={t('answers_shown')}
                inputProps={{ min: 1, max: task.choices.length }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: theme.palette.background.default,
                    width: '8rem',
                    marginLeft: '1rem',
                  },
                }}
                value={task.num_choice}
                onChange={(e) =>
                  task.set('num_choice', Number(e.target.value), true)
                }
              />
            </Box>
          </Box>
        )}
        <Box>
          <Button
            color='secondary'
            startIcon={<Icon iconId='bin' />}
            disabled={!course?.canEdit}
            onClick={() => course?.getCurStep?.quiz.removeTask(task)}
          >
            {t('delete')}
          </Button>
          <FormControlLabel
            label={t('always_visible') as string}
            control={
              <Switch
                disabled={!course?.canEdit}
                checked={task.always_show}
                name='mandatory'
                onChange={() =>
                  task.set('always_show', !task.always_show, true)
                }
              />
            }
          />
        </Box>
      </Box>
      {!task.isValid && (
        <Box
          p={1}
          mt={3}
          width='fit-content'
          sx={{
            backgroundColor: 'rgba(171, 35, 40, 0.2)',
            borderRadius: '6px',
          }}
        >
          <Typography color={theme.palette.error.main} variant='body1'>
            {t(task.getValidationError, { ns: 'validation' })}
          </Typography>
        </Box>
      )}
    </Paper>
  )
});